import React from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import { EmptyNote } from '../ui/EmptyNote';
import { LoadRow } from '../LoadRow';
import { PanelSection } from '../ui/PanelSection';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { LoadSheet } from '../sheets/LoadSheet';
import { TargetPickerSheet } from '../sheets/TargetPickerSheet';
import type { Load } from '../../../lib/rcu/model';
import { isWired } from '../../../lib/rcu/model';
import { findRoom } from '../../../lib/rcu/query/lookup';
import { targetOfLoad } from '../../../lib/rcu/query/targets';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useLoadStateStore } from '../../../stores/loadStateStore';
import { useCommandFeedback } from '../useCommandFeedback';

type Filter = 'all' | 'wired' | 'unwired';

const FILTERS: readonly { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'wired', label: 'Wired' },
  { id: 'unwired', label: 'Not wired' },
];

const matches = (load: Load, filter: Filter, query: string): boolean => {
  const wired = isWired(load);
  if (filter === 'wired' && !wired) return false;
  if (filter === 'unwired' && wired) return false;
  return query.trim() === '' || load.name.toLowerCase().includes(query.trim().toLowerCase());
};

/** Every load in the configuration, wired or not. */
export const LoadsPanel: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const removeLoad = useRcuConfigStore((state) => state.removeLoad);
  const forget = useLoadStateStore((state) => state.forget);
  const report = useCommandFeedback();

  const [filter, setFilter] = React.useState<Filter>('all');
  const [query, setQuery] = React.useState('');
  const [editing, setEditing] = React.useState<{ load: Load | null } | null>(null);
  const [wiring, setWiring] = React.useState<Load | null>(null);
  const [removing, setRemoving] = React.useState<Load | null>(null);

  const visible = config.loads.filter((load) => matches(load, filter, query));

  return (
    <div className="space-y-4">
      <PanelSection
        title="Loads"
        description={`${config.loads.length} loads · ${config.loads.filter(isWired).length} wired`}
        action={
          <Button variant="primary" size="sm" onClick={() => setEditing({ load: null })}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add load
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setFilter(option.id)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                filter === option.id
                  ? 'bg-[#D4A054] text-white'
                  : 'bg-[#F5F5F5] text-[#666666] hover:bg-[#E0E0E0]'
              }`}
            >
              {option.label}
            </button>
          ))}

          <span className="relative ml-auto flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-[#999999]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search loads"
              className="h-10 w-52 rounded-full border border-[#E0E0E0] bg-white pl-9 pr-4 text-sm outline-none focus:border-[#D4A054]"
            />
          </span>
        </div>

        {visible.length === 0 ? (
          <EmptyNote
            message={
              config.loads.length === 0
                ? 'No loads yet. Add one, then wire it to an output.'
                : 'No loads match this filter.'
            }
          />
        ) : (
          <ul className="space-y-2">
            {visible.map((load) => (
              <LoadRow
                key={load.id}
                load={load}
                roomName={load.roomId === null ? null : findRoom(config, load.roomId)?.name ?? null}
                target={targetOfLoad(config, load)}
                onEdit={() => setEditing({ load })}
                onAssign={() => setWiring(load)}
                onRemove={() => setRemoving(load)}
              />
            ))}
          </ul>
        )}
      </PanelSection>

      {editing !== null && (
        <LoadSheet
          isOpen
          load={editing.load}
          channel={editing.load?.channel ?? null}
          onClose={() => setEditing(null)}
        />
      )}

      {wiring !== null && (
        <TargetPickerSheet isOpen load={wiring} onClose={() => setWiring(null)} />
      )}

      <ConfirmDialog
        isOpen={removing !== null}
        title="Remove load"
        message={
          removing === null
            ? ''
            : `${removing.name} will be deleted from this configuration. Its output becomes free.`
        }
        confirmLabel="Remove load"
        destructive
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing === null) return;
          if (report(removeLoad(removing.id), 'Load removed.')) forget(removing.id);
          setRemoving(null);
        }}
      />
    </div>
  );
};

export default LoadsPanel;
